import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import BottomNav from "@/components/BottomNav";
import { ArrowLeft, Trash2, Check, Mail, ShieldCheck, Tag, Plus, X, Filter, Send, Loader2, Reply, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

// Color-code preset tags for at-a-glance triage
const TAG_STYLES = {
  vip:    "bg-amber-500/15 border-amber-400/40 text-amber-200",
  beta:   "bg-sky-500/15 border-sky-400/40 text-sky-200",
  banned: "bg-rose-500/15 border-rose-400/40 text-rose-200",
  spam:   "bg-neutral-500/15 border-neutral-400/30 text-neutral-300",
  friend: "bg-emerald-500/15 border-emerald-400/40 text-emerald-200",
};
const DEFAULT_TAG_STYLE = "bg-violet-500/15 border-violet-400/40 text-violet-200";
const tagStyle = (t) => TAG_STYLES[t.toLowerCase()] || DEFAULT_TAG_STYLE;

export default function AdminInbox() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [presetTags, setPresetTags] = useState(["vip", "beta", "banned", "spam", "friend"]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [tagPickerFor, setTagPickerFor] = useState(null); // feedback.id whose sender's tags we're editing
  const [replyFor, setReplyFor] = useState(null); // feedback.id currently composing a reply for
  const [replyDraft, setReplyDraft] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  // Filter: "all" | "unread" | tag_name. Any tag ever seen on the inbox
  // (including custom ones) becomes a filter option automatically.
  const [filter, setFilter] = useState("all");

  // Multi-select mode — admins can bulk-delete feedback. Selection is a Set of ids.
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  function toggleSelect(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function exitSelectMode() {
    setSelectMode(false);
    setSelectedIds(new Set());
  }
  async function bulkDelete() {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (!confirm(`Delete ${ids.length} message${ids.length === 1 ? "" : "s"}? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      const results = await Promise.allSettled(ids.map((id) => api.delete(`/admin/feedback/${id}`)));
      const okIds = ids.filter((_, i) => results[i].status === "fulfilled");
      const failed = ids.length - okIds.length;
      setItems((prev) => prev.filter((x) => !okIds.includes(x.id)));
      exitSelectMode();
      if (failed) toast.error(`Deleted ${okIds.length}. ${failed} failed.`);
      else toast.success(`Deleted ${okIds.length} message${okIds.length === 1 ? "" : "s"}.`);
    } finally {
      setBulkDeleting(false);
    }
  }

  const load = () => {
    setLoading(true);
    api.get("/admin/feedback")
      .then(({ data }) => {
        setItems(data.feedback || []);
        if (Array.isArray(data.preset_tags) && data.preset_tags.length) {
          setPresetTags(data.preset_tags);
        }
      })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Access denied."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (user === null) return; // still checking session — don't redirect prematurely
    if (!user?.is_admin) {
      nav("/discover", { replace: true });
      return;
    }
    load();
  }, [user, nav]);

  async function markRead(id) {
    try {
      await api.patch(`/admin/feedback/${id}/read`);
      setItems((prev) => prev.map((x) => (x.id === id ? { ...x, read: true } : x)));
    } catch {}
  }
  async function del(id) {
    if (!confirm("Delete this message?")) return;
    await api.delete(`/admin/feedback/${id}`);
    setItems((prev) => prev.filter((x) => x.id !== id));
  }

  // Save the full tag list for the sender of a given feedback item.
  async function saveSenderTags(feedbackItem, nextTags) {
    try {
      const { data } = await api.put(`/admin/users/${feedbackItem.user_id}/tags`, { tags: nextTags });
      // Propagate to every visible feedback item from the same sender.
      setItems((prev) => prev.map((x) => (x.user_id === feedbackItem.user_id ? { ...x, sender_tags: data.tags } : x)));
      toast.success("Tags updated.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save tags.");
    }
  }

  async function sendReply(feedbackItem) {
    const body = replyDraft.trim();
    if (!body) return;
    setSendingReply(true);
    try {
      const { data } = await api.post(`/admin/feedback/${feedbackItem.id}/reply`, { message: body });
      if (data?.ok === false) {
        // Backend returned 200 with a soft error (e.g. Resend testing-mode restriction).
        toast.error(data.error || "Could not send reply.");
        return;
      }
      setItems((prev) => prev.map((x) => (
        x.id === feedbackItem.id
          ? { ...x, read: true, replies: [...(x.replies || []), data.reply], last_reply_at: data.reply.sent_at }
          : x
      )));
      setReplyDraft("");
      setReplyFor(null);
      toast.success("Reply sent.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send reply.");
    } finally {
      setSendingReply(false);
    }
  }

  // Build the set of tag filters from what actually appears in the inbox
  // (union of preset tags + any custom tags seen on any sender).
  const availableTagFilters = useMemo(() => {
    const seen = new Set(presetTags);
    for (const f of items) for (const t of (f.sender_tags || [])) seen.add(t);
    return Array.from(seen);
  }, [items, presetTags]);

  const unreadCount = useMemo(() => items.filter((f) => !f.read).length, [items]);

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    if (filter === "unread") return items.filter((f) => !f.read);
    return items.filter((f) => (f.sender_tags || []).includes(filter));
  }, [items, filter]);

  if (!user?.is_admin) return null;

  return (
    <div className="mx-auto max-w-2xl px-4 pb-32 pt-safe-6">
      <header className="mb-6 flex items-center gap-3">
        <button data-testid="admin-back-btn" onClick={() => nav(-1)} className="flex h-10 w-10 items-center justify-center rounded-full bg-white/5 text-white/70 hover:bg-white/10">
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <div className="font-display text-xs uppercase tracking-[0.35em] text-amber-300/80 flex items-center gap-2">
            <ShieldCheck size={12} /> Admin
          </div>
          <h1 className="font-display text-3xl leading-tight text-white">Feedback Inbox</h1>
        </div>
        {items.length > 0 && (
          selectMode ? (
            <button
              data-testid="admin-select-cancel-btn"
              onClick={exitSelectMode}
              className="flex h-10 items-center gap-1.5 rounded-full bg-white/5 border border-white/10 px-3 text-xs text-neutral-300 hover:bg-white/10"
            >
              <X size={12} /> Cancel
            </button>
          ) : (
            <button
              data-testid="admin-select-toggle-btn"
              onClick={() => setSelectMode(true)}
              className="flex h-10 items-center gap-1.5 rounded-full bg-white/5 border border-white/10 px-3 text-xs text-neutral-300 hover:bg-white/10"
              title="Select multiple to delete"
            >
              <Check size={12} /> Select
            </button>
          )
        )}
        <button
          data-testid="admin-usage-link"
          onClick={() => nav("/admin/usage")}
          className="flex h-10 items-center gap-1.5 rounded-full bg-amber-500/10 border border-amber-400/30 px-3 text-xs text-amber-200 hover:bg-amber-500/20"
          title="Cost & usage dashboard"
        >
          <TrendingUp size={12} /> Usage
        </button>
      </header>

      {loading ? (
        <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-16 animate-pulse rounded-2xl bg-white/[0.03]" />)}</div>
      ) : items.length === 0 ? (
        <div data-testid="admin-empty" className="rounded-2xl border border-dashed border-white/10 p-8 text-center">
          <Mail size={36} className="mx-auto text-neutral-700" />
          <div className="mt-3 font-display text-xl text-white">No feedback yet.</div>
          <p className="mt-1 text-sm text-neutral-500">When users send you messages, they'll show up here.</p>
        </div>
      ) : (
        <>
          {/* Filter row — quick triage by unread status or sender tag */}
          <div className="mb-3 flex items-center gap-2 overflow-x-auto pb-1" data-testid="admin-filter-row">
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-neutral-500 shrink-0">
              <Filter size={11} /> Show
            </span>
            <FilterChip
              testId="admin-filter-all"
              active={filter === "all"}
              onClick={() => setFilter("all")}
              label={`All (${items.length})`}
            />
            <FilterChip
              testId="admin-filter-unread"
              active={filter === "unread"}
              onClick={() => setFilter("unread")}
              label={`Unread (${unreadCount})`}
              tone="violet"
            />
            {availableTagFilters.map((t) => {
              const count = items.filter((f) => (f.sender_tags || []).includes(t)).length;
              if (count === 0) return null;
              return (
                <FilterChip
                  key={t}
                  testId={`admin-filter-tag-${t}`}
                  active={filter === t}
                  onClick={() => setFilter(t)}
                  label={`${t} (${count})`}
                  tagName={t}
                />
              );
            })}
          </div>

          {filtered.length === 0 ? (
            <div data-testid="admin-filter-empty" className="rounded-2xl border border-dashed border-white/10 p-6 text-center text-sm text-neutral-500">
              No feedback matches this filter.
            </div>
          ) : (
            <ul className="space-y-2" data-testid="admin-inbox-list">
              {filtered.map((f) => {
                const isSelected = selectedIds.has(f.id);
                return (
            <li key={f.id} className={`rounded-2xl border p-4 ${
              isSelected
                ? "border-rose-400/60 bg-rose-500/[0.06]"
                : f.read ? "border-white/5 bg-white/[0.02]" : "border-violet-500/30 bg-violet-500/[0.05]"
            }`}>
              <div className="flex items-start gap-3">
                {selectMode && (
                  <button
                    data-testid={`admin-select-checkbox-${f.id}`}
                    onClick={() => toggleSelect(f.id)}
                    aria-pressed={isSelected}
                    aria-label={isSelected ? "Deselect message" : "Select message"}
                    className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border transition ${
                      isSelected
                        ? "border-rose-400 bg-rose-500/40 text-white"
                        : "border-white/20 bg-white/[0.03] hover:border-white/40"
                    }`}
                  >
                    {isSelected && <Check size={12} />}
                  </button>
                )}
                <button
                  onClick={() => {
                    if (selectMode) { toggleSelect(f.id); return; }
                    setOpenId(openId === f.id ? null : f.id);
                    if (!f.read) markRead(f.id);
                  }}
                  className="flex-1 text-left"
                  data-testid={`admin-msg-${f.id}`}
                >
                  <div className="flex items-center gap-2">
                    {!f.read && <span className="h-2 w-2 rounded-full bg-violet-400" aria-hidden />}
                    <span className="font-medium text-white truncate">{f.subject}</span>
                    <span className="ml-auto text-[11px] text-neutral-500">{f.created_at && formatDistanceToNow(new Date(f.created_at), { addSuffix: true })}</span>
                  </div>
                  <div className="mt-0.5 text-xs text-neutral-400 flex items-center gap-2 flex-wrap">
                    <span>@{f.username} · {f.user_email}</span>
                    {(f.sender_tags || []).map((t) => (
                      <span
                        key={t}
                        data-testid={`admin-sender-tag-${f.id}-${t}`}
                        className={`inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] uppercase tracking-widest ${tagStyle(t)}`}
                      >{t}</span>
                    ))}
                  </div>
                </button>
              </div>
              {openId === f.id && !selectMode && (
                <div className="mt-3">
                  <div className="rounded-xl border border-white/5 bg-white/[0.02] p-3 text-sm text-neutral-200 whitespace-pre-wrap">{f.body}</div>

                  {/* Sender tags editor */}
                  <div className="mt-3 rounded-xl border border-white/5 bg-white/[0.02] p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-neutral-500">
                        <Tag size={11} /> Sender tags
                      </div>
                      <button
                        data-testid={`admin-tag-toggle-${f.id}`}
                        onClick={() => setTagPickerFor(tagPickerFor === f.id ? null : f.id)}
                        className="rounded-full bg-white/5 border border-white/10 px-2.5 py-1 text-[11px] text-neutral-300 hover:bg-white/10 hover:text-white transition"
                      >
                        {tagPickerFor === f.id ? "Done" : "Edit"}
                      </button>
                    </div>

                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {(f.sender_tags || []).length === 0 && tagPickerFor !== f.id && (
                        <span className="text-xs text-neutral-500">No tags yet.</span>
                      )}
                      {(f.sender_tags || []).map((t) => (
                        <span key={t} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${tagStyle(t)}`}>
                          {t}
                          {tagPickerFor === f.id && (
                            <button
                              data-testid={`admin-tag-remove-${f.id}-${t}`}
                              onClick={() => saveSenderTags(f, (f.sender_tags || []).filter((x) => x !== t))}
                              className="ml-0.5 opacity-70 hover:opacity-100"
                              aria-label={`Remove tag ${t}`}
                            ><X size={10} /></button>
                          )}
                        </span>
                      ))}
                    </div>

                    {tagPickerFor === f.id && (
                      <TagAddRow
                        feedback={f}
                        presets={presetTags}
                        onAdd={(t) => saveSenderTags(f, Array.from(new Set([...(f.sender_tags || []), t.toLowerCase()])))}
                      />
                    )}
                  </div>

                  {/* Reply history (if any replies have been sent by admin) */}
                  {(f.replies || []).length > 0 && (
                    <div className="mt-3 rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] p-3" data-testid={`admin-reply-history-${f.id}`}>
                      <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-emerald-300/80">
                        <Reply size={11} /> Replies sent ({f.replies.length})
                      </div>
                      <ul className="mt-2 space-y-2">
                        {f.replies.map((r, idx) => (
                          <li key={idx} className="rounded-lg bg-black/20 p-2 text-xs text-neutral-200 whitespace-pre-wrap">
                            <div className="mb-1 text-[10px] uppercase tracking-widest text-neutral-500">
                              {r.sent_at && formatDistanceToNow(new Date(r.sent_at), { addSuffix: true })} · {r.admin_email || "admin"}
                            </div>
                            {r.message}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Inline reply composer */}
                  {replyFor === f.id && (
                    <div className="mt-3 rounded-xl border border-violet-500/30 bg-violet-500/[0.05] p-3" data-testid={`admin-reply-composer-${f.id}`}>
                      <div className="mb-1.5 text-xs uppercase tracking-widest text-violet-200/80">
                        Reply to @{f.username}
                      </div>
                      <textarea
                        data-testid={`admin-reply-textarea-${f.id}`}
                        value={replyDraft}
                        onChange={(e) => setReplyDraft(e.target.value)}
                        rows={5}
                        maxLength={4000}
                        placeholder={`Hi @${f.username},\n\nThanks for reaching out. …`}
                        className="w-full resize-none rounded-lg border border-white/10 bg-black/30 p-2.5 text-sm text-white outline-none placeholder:text-neutral-600 focus:border-violet-500/50"
                        autoFocus
                      />
                      <div className="mt-2 flex items-center justify-between text-[10px] text-neutral-500">
                        <span>Delivered via email · Reply-to: {user?.email}</span>
                        <span>{replyDraft.length}/4000</span>
                      </div>
                      <div className="mt-2 flex gap-2">
                        <button
                          data-testid={`admin-reply-send-${f.id}`}
                          disabled={!replyDraft.trim() || sendingReply}
                          onClick={() => sendReply(f)}
                          className="btn-pill btn-primary text-xs px-3 py-1.5 disabled:opacity-50"
                        >
                          {sendingReply ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                          {sendingReply ? "Sending…" : "Send reply"}
                        </button>
                        <button
                          data-testid={`admin-reply-cancel-${f.id}`}
                          onClick={() => { setReplyFor(null); setReplyDraft(""); }}
                          className="btn-pill btn-ghost text-xs px-3 py-1.5"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="mt-2 flex gap-2">
                    {replyFor !== f.id && (
                      <button
                        data-testid={`admin-reply-${f.id}`}
                        onClick={() => { setReplyFor(f.id); setReplyDraft(""); }}
                        className="btn-pill btn-primary text-xs px-3 py-1.5"
                      >
                        <Mail size={12} /> Reply via email
                      </button>
                    )}
                    <button
                      data-testid={`admin-delete-${f.id}`}
                      onClick={() => del(f.id)}
                      className="btn-pill text-xs px-3 py-1.5 bg-rose-500/10 border border-rose-500/30 text-rose-300 hover:bg-rose-500/15"
                    >
                      <Trash2 size={12} />
                    </button>
                    {!f.read && (
                      <button data-testid={`admin-markread-${f.id}`} onClick={() => markRead(f.id)} className="btn-pill btn-ghost text-xs px-3 py-1.5">
                        <Check size={12} /> Mark read
                      </button>
                    )}
                  </div>
                </div>
              )}
            </li>
                );
              })}
            </ul>
          )}
        </>
      )}

      {/* Floating bulk-delete action bar — visible when in select mode */}
      {selectMode && (
        <div
          data-testid="admin-bulk-action-bar"
          className="fixed inset-x-0 bottom-24 z-40 mx-auto flex max-w-md items-center justify-between gap-3 rounded-2xl border border-rose-400/40 bg-rose-950/85 px-4 py-3 shadow-2xl backdrop-blur"
          style={{ left: 16, right: 16 }}
        >
          <div className="text-sm text-rose-100">
            <span data-testid="admin-selected-count" className="font-semibold">{selectedIds.size}</span>{" "}
            selected
          </div>
          <div className="flex gap-2">
            <button
              data-testid="admin-bulk-delete-btn"
              disabled={selectedIds.size === 0 || bulkDeleting}
              onClick={bulkDelete}
              className="btn-pill text-xs px-3 py-1.5 bg-rose-500/30 border border-rose-400/60 text-white hover:bg-rose-500/40 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {bulkDeleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
              {bulkDeleting ? "Deleting…" : "Delete"}
            </button>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
}

function FilterChip({ testId, active, onClick, label, tone, tagName }) {
  // Preset tag colors mirror TAG_STYLES so the pill matches the sender chips.
  const activeClass = tagName
    ? tagStyle(tagName) + " ring-1 ring-white/20"
    : tone === "violet"
      ? "bg-violet-500/25 border-violet-400/50 text-violet-100 ring-1 ring-white/20"
      : "bg-white/15 border-white/25 text-white ring-1 ring-white/20";
  const idleClass = tagName
    ? "border-white/10 bg-white/[0.03] text-neutral-400 hover:bg-white/[0.06] hover:text-white"
    : "border-white/10 bg-white/[0.03] text-neutral-400 hover:bg-white/[0.06] hover:text-white";
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`shrink-0 inline-flex items-center rounded-full border px-3 py-1 text-[11px] uppercase tracking-widest transition ${active ? activeClass : idleClass}`}
    >
      {label}
    </button>
  );
}

function TagAddRow({ feedback, presets, onAdd }) {
  const [custom, setCustom] = useState("");
  const current = new Set((feedback.sender_tags || []).map((t) => t.toLowerCase()));
  const available = presets.filter((p) => !current.has(p));
  return (
    <div className="mt-3 border-t border-white/5 pt-3">
      {available.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {available.map((p) => (
            <button
              key={p}
              data-testid={`admin-tag-preset-${feedback.id}-${p}`}
              onClick={() => onAdd(p)}
              className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] hover:brightness-125 transition ${tagStyle(p)}`}
            >
              <Plus size={10} /> {p}
            </button>
          ))}
        </div>
      )}
      <div className="flex gap-1.5">
        <input
          data-testid={`admin-tag-custom-input-${feedback.id}`}
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          maxLength={24}
          placeholder="Custom tag…"
          className="flex-1 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[12px] text-white outline-none focus:border-violet-500/50"
          onKeyDown={(e) => {
            if (e.key === "Enter" && custom.trim()) {
              onAdd(custom.trim());
              setCustom("");
            }
          }}
        />
        <button
          data-testid={`admin-tag-custom-add-${feedback.id}`}
          onClick={() => { if (custom.trim()) { onAdd(custom.trim()); setCustom(""); } }}
          className="rounded-full bg-violet-500/25 border border-violet-500/40 px-3 py-1 text-[12px] text-violet-100 hover:bg-violet-500/35"
        >Add</button>
      </div>
    </div>
  );
}
